import { NavLink, useLocation } from 'react-router-dom';
import { CheckSquare, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/store/appStore';

/**
 * Bottom tab bar (mobile) + floating pill nav (desktop).
 *
 * Mobile redesign 2026-06-15:
 *   • Side icons used to be naked SVG, easy to mistake for decoration. They're
 *     now 44 × 44 glass pill-buttons with a clear border — iOS HIG min
 *     tap-target, and obviously interactive.
 *   • Center "Assistentus" button keeps its identity (avatar + glow) but
 *     calms down when not on /chat so the active SIDE tab pops harder.
 *   • Active side tab gets the full Tiffany treatment (border + bg + glow);
 *     inactive side stays muted. Center is now the dimmer one when the user
 *     is in Tasks or Notes — the "you are here" cue should win.
 *   • Side tabs are pushed further away from center (gap-3 → gap-6 on mobile)
 *     so taps don't accidentally land between buttons.
 *   • The bar itself is no longer a flat gray slab: vertical gradient + a
 *     thin Tiffany accent line at the top edge.
 *
 * Desktop layout (md+) is untouched — user explicitly said it's good.
 */
export function BottomTabBar() {
  const { pathname } = useLocation();
  const setActivePanel = useAppStore((s) => s.setActivePanel);

  const isTask = pathname.startsWith('/tasks');
  const isChat = pathname.startsWith('/chat');
  const isNotes = pathname.startsWith('/notes');

  return (
    <div
      className={cn(
        'fixed z-[9999] pointer-events-none',
        'bottom-0 left-0 right-0',
        'md:bottom-auto md:top-3 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-auto',
        // Auto-hide the tab bar when the iOS keyboard is open. The user
        // is actively typing in a chat / search / form — they don't need
        // module-switch chrome competing with the keyboard for vertical
        // space. `kb-open` is set on <html> by installKeyboardSync; the
        // slide-down matches the keyboard's own animation timing so the
        // two motions read as one. Tap anywhere outside the input to close
        // the keyboard and the tab bar slides back up.
        'transition-transform duration-200 ease-out',
        '[html.kb-open_&]:translate-y-full',
      )}
    >
      {/* Background layer.
          MOBILE: vertical gradient (denser at bottom for legibility behind
                  the home indicator) + 1px Tiffany glow line on the top edge.
          DESKTOP (md+): single rounded glass pill — original look. */}
      <div
        className={cn(
          'absolute inset-0 pointer-events-auto backdrop-blur-xl',
          // Mobile-only chrome
          'bg-gradient-to-t from-black/90 via-black/75 to-black/55',
          'before:absolute before:left-0 before:right-0 before:top-0 before:h-px',
          'before:bg-gradient-to-r before:from-transparent before:via-[#0abab5]/55 before:to-transparent',
          'before:content-[""]',
          // Desktop pill overrides
          'md:bg-black/60 md:rounded-full md:border md:border-white/10',
          'md:before:hidden',
        )}
      />

      <nav
        className={cn(
          'relative flex items-center justify-center pointer-events-auto',
          // Mobile: wider gaps so the side buttons don't crowd the center
          'gap-6 px-5 py-2',
          // Desktop: original compact spacing
          'md:gap-6 md:px-6 md:py-1.5',
        )}
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))' }}
      >
        <SideTab
          to="/tasks"
          label="Tasks"
          isActive={isTask}
          onClick={() => setActivePanel('tasks')}
          icon={<CheckSquare className="w-[18px] h-[18px] md:w-5 md:h-5" />}
        />

        <NavLink
          to="/chat"
          onClick={() => setActivePanel('chat')}
          className="group flex items-center justify-center -my-3 md:-my-2 transition-all duration-300"
          title="Assistentus"
        >
          <div
            className={cn(
              'w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 overflow-hidden',
              isChat ? 'scale-110' : 'hover:scale-105',
            )}
            style={{
              // Solid dark-Tiffany radial gradient gives the logo a self-
              // contained "coin" look on top of the translucent tab bar.
              // Without this, the bar bleeds through the transparent PNG
              // and the button reads as a floating sticker instead of a
              // single piece (user feedback 2026-06-23).
              background:
                'radial-gradient(circle at 35% 30%, #0f3835 0%, #082624 55%, #04181a 100%)',
              boxShadow: isChat
                ? '0 0 0 2px #0abab5, 0 0 20px 4px rgba(10,186,181,0.6), 0 0 40px 8px rgba(10,186,181,0.2), inset 0 1px 0 rgba(255,255,255,0.08)'
                : '0 0 0 1.5px rgba(10,186,181,0.32), 0 0 10px 1px rgba(10,186,181,0.12), inset 0 1px 0 rgba(255,255,255,0.06)',
              opacity: isChat ? 1 : 0.92,
            }}
          >
            <img
              src="/assistentus-neon.png"
              alt="Assistentus"
              className="w-full h-full object-cover"
            />
          </div>
        </NavLink>

        <SideTab
          to="/notes"
          label="Notes"
          isActive={isNotes}
          onClick={() => setActivePanel('notes')}
          icon={<FileText className="w-[18px] h-[18px] md:w-5 md:h-5" />}
        />
      </nav>
    </div>
  );
}

/**
 * Side tab — pill button on mobile, naked icon on desktop.
 *
 * Active mobile state stacks: Tiffany border + tinted fill + drop-shadow
 * glow + 105% scale. Inactive is a quiet glass chip that still reads as a
 * button. Desktop drops all the chrome so the floating top pill stays
 * minimal as before.
 */
function SideTab({
  to,
  label,
  icon,
  isActive,
  onClick,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick?: () => void;
}) {
  return (
    <NavLink to={to} onClick={onClick} title={label} className="group flex items-center justify-center">
      <div
        className={cn(
          'flex items-center justify-center transition-all duration-300',
          // Mobile pill (44×44, iOS HIG min tap target)
          'w-11 h-11 rounded-2xl border',
          isActive
            ? 'bg-[#0abab5]/18 border-[#0abab5] text-[#0abab5] shadow-[0_0_18px_rgba(10,186,181,0.5)] scale-105'
            : 'bg-white/[0.04] border-white/10 text-white/45 group-hover:bg-white/[0.08] group-hover:border-white/20 group-hover:text-white/80',
          // Desktop: drop the pill entirely, original transparent icon-only look
          'md:w-auto md:h-auto md:rounded-none md:border-0 md:bg-transparent md:shadow-none md:scale-100',
          'md:p-0',
          isActive
            ? 'md:text-[#0abab5] md:drop-shadow-[0_0_8px_rgba(10,186,181,0.8)]'
            : 'md:text-white/40 md:group-hover:text-white/80 md:bg-transparent',
        )}
      >
        {icon}
      </div>
    </NavLink>
  );
}
